import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../config/api';
import './PendingValidationSlider.css';

const formatRelativeDate = (value) => {
  if (!value) return 'Recently updated';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently updated';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const PendingValidationSlider = ({
  title = 'Pending human validation',
  subtitle = 'Experiments with steps still awaiting review. Most recent first.',
  className = '',
  limit = 24
}) => {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setCanScrollLeft(track.scrollLeft > 4);
    setCanScrollRight(maxScroll - track.scrollLeft > 4);
  }, []);

  const fetchPending = useCallback(async (signal) => {
    const fetchId = ++fetchSeqRef.current;
    const isInitialLoad = fetchId === 1;

    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setLoadError('');

      const response = await API.get('/qurator/human-validation/pending-experiments', {
        params: { limit },
        signal
      });

      if (fetchId !== fetchSeqRef.current) return;

      if (response.data?.success) {
        setExperiments(Array.isArray(response.data.experiments) ? response.data.experiments : []);
      } else {
        setLoadError(response.data?.message || 'Could not load pending validations.');
      }
    } catch (error) {
      if (fetchId !== fetchSeqRef.current) return;
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;

      console.error('Failed to load pending validation experiments:', error);
      setLoadError(error.response?.data?.message || 'Could not load pending validations.');
    } finally {
      if (fetchId === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [limit]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPending(controller.signal);
    return () => {
      controller.abort();
      fetchSeqRef.current += 1;
    };
  }, [fetchPending]);

  useEffect(() => {
    updateScrollState();
    const track = trackRef.current;
    if (!track) return undefined;
    track.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      track.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [experiments, loading, updateScrollState]);

  const scrollByAmount = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  const handleValidate = (experiment) => {
    const step = experiment.next_step || 1;
    navigate(`/human-validation?editid=${experiment.prt_id}&step=${step}`);
  };

  const sectionClassName = `pv-slider-section ${className}`.trim();

  if (loading && experiments.length === 0) {
    return (
      <section className={sectionClassName}>
        <div className="pv-slider-head">
          <div>
            <h2 className="pv-slider-title">{title}</h2>
            <p className="pv-slider-subtitle">{subtitle}</p>
          </div>
        </div>
        <div className="pv-slider-loading">
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <span>Loading pending validations…</span>
        </div>
      </section>
    );
  }

  if (!experiments.length) {
    return null;
  }

  return (
    <section className={sectionClassName}>
      <div className="pv-slider-head">
        <div>
          <h2 className="pv-slider-title">
            <i className="bi bi-person-check-fill" aria-hidden="true" />
            {title}
          </h2>
          <p className="pv-slider-subtitle">{subtitle}</p>
        </div>
        <div className="pv-slider-nav">
          <button
            type="button"
            className="pv-slider-nav-btn"
            onClick={() => scrollByAmount(-1)}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
          >
            <i className="bi bi-chevron-left" />
          </button>
          <button
            type="button"
            className="pv-slider-nav-btn"
            onClick={() => scrollByAmount(1)}
            disabled={!canScrollRight}
            aria-label="Scroll right"
          >
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      </div>

      {loadError && (
        <div className="pv-slider-error" role="status">
          <i className="bi bi-exclamation-circle" />
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => fetchPending(undefined)}>
            Retry
          </button>
        </div>
      )}

      <div className="pv-slider-track-wrap">
        <div className="pv-slider-track" ref={trackRef}>
          {experiments.map((experiment) => {
            const total = Number(experiment.total_steps) || 0;
            const validated = Number(experiment.validated_steps) || 0;
            const progress = total > 0 ? Math.round((validated / total) * 100) : 0;
            const remaining = Math.max(total - validated, 0);

            return (
              <article key={experiment.prt_id} className="pv-slider-card">
                <div className="pv-slider-card-top">
                  <span className="pv-slider-badge">{remaining} step{remaining === 1 ? '' : 's'} left</span>
                  <span className="pv-slider-date">{formatRelativeDate(experiment.date)}</span>
                </div>
                <h3 className="pv-slider-card-title">{experiment.draft_title}</h3>
                {experiment.prt_description && (
                  <p className="pv-slider-card-desc">{experiment.prt_description}</p>
                )}
                <div className="pv-slider-progress-wrap">
                  <div className="pv-slider-progress-meta">
                    <span>{validated} of {total} validated</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="pv-slider-progress-bar" aria-hidden="true">
                    <div className="pv-slider-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary pv-slider-validate-btn"
                  onClick={() => handleValidate(experiment)}
                >
                  <i className="bi bi-shield-check me-2" />
                  Validate
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PendingValidationSlider;
